import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ImageButtons } from 'components/Field/ImageButtons';
import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { UserAvatar } from 'components/UserAvatar';
import { useConfirm } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import { selectHasSidebarNavigation } from 'store/app/selectors';
import { SIGN_OUT } from 'store/auth/actions';
import { selectUser } from 'store/auth/selectors';
import {
  REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD,
  REMOTE_ACCOUNT_LOAD_DETAILS,
  REMOTE_ACCOUNT_LOAD_TFA,
  REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD,
  REMOTE_ACCOUNT_PASSWORD_DELETE,
  REMOTE_ACCOUNT_SAVE_AVATAR,
} from 'store/remote/actions';

export const ProfileScreen: React.FC = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const user = useSelector(selectUser);
  const hasSidebarNavigation = useSelector(selectHasSidebarNavigation);

  const handleGoPasswordless = () => {
    confirm({
      title: 'Switch to Passwordless',
      confirmText: 'Switch',
      text:
        'Instead of remembering a password, go passwordless and RiskBase will email you a 6 digit code when you sign in\n\n' +
        'This is a one-way change and cannot be reversed',
      onConfirm: () => dispatch(REMOTE_ACCOUNT_PASSWORD_DELETE()),
    });
  };

  if (!user) {
    return null;
  }

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Profile"
          showSidebarButton
          // Get help button (mobile only)
          onPressCloseButton={
            !hasSidebarNavigation
              ? () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.Support,
                      params: {},
                    }),
                  )
              : undefined
          }
          closeIcon={ICONS.CommentQuestionLight}
        />
      }
    >
      <View style={{ alignItems: 'center' }}>
        <UserAvatar size={200} />
        <View
          style={{
            alignItems: 'center',
            flexDirection: 'row',
            marginTop: -theme.grid * 2,
          }}
        >
          <ImageButtons
            enableRemoveButton={!!user.avatar}
            removeConfirmMessage="Are you sure you want to remove your avatar?"
            action={REMOTE_ACCOUNT_SAVE_AVATAR({ image: null })}
          />
        </View>
      </View>

      <Card>
        <CardRow
          label="Details"
          value={user.name}
          onPress={() => dispatch(REMOTE_ACCOUNT_LOAD_DETAILS())}
        />
        <CardRow
          label="Email"
          value={user.email}
          onPress={() => dispatch(REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD())}
        />
      </Card>

      {user.ofa !== 'password' && (
        <Card
          label="Password"
          explanation="RiskBase emails you a 6 digit code when you sign in"
        >
          <CardRow label="Your Account is Passwordless" />
        </Card>
      )}

      {user.ofa === 'password' && (
        <Card
          label="Password"
          explanation="Instead of remembering a password, go passwordless. RiskBase will email you a 6 digit code when you sign in"
        >
          <CardRow
            label="Change Your Password"
            onPress={() => dispatch(REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD())}
          />
          <CardRow
            label="Switch to Passwordless"
            onPress={handleGoPasswordless}
            labelAlignTop
          />
        </Card>
      )}

      <Card
        label="Multi-Factor Authentication"
        explanation={
          !user.tfa
            ? user.tfaRequired
              ? 'Your account requires multi-factor authentication. Set it up now to add an extra level of security to your account'
              : 'Setup multi-factor authentication to add an extra level of security to your account'
            : 'Multi-Factor authentication is setup on your RiskBase account'
        }
      >
        <CardRow
          label={user.tfa ? 'See details...' : 'Setup now...'}
          labelAlignTop
          onPress={() => dispatch(REMOTE_ACCOUNT_LOAD_TFA())}
        />
      </Card>

      <Card>
        <CardRow
          label="Sign Out"
          icon={ICONS.PowerOffLight}
          iconColor={theme.colors.red}
          labelColor={theme.colors.red}
          onPress={() => dispatch(SIGN_OUT.request())}
        />
      </Card>
    </Page>
  );
};
